/**
 * Build the create-project request payload from raw modal state.
 *
 * Centralises trim/omit semantics and input-unit conversions. Amount fields
 * are typed in rupees and sent to the API as integer paisa. Percent fields are
 * typed as user-facing percentages and sent as basis points (100.00% = 10000).
 */
import { parsePercentInputToBps, parseRupeeInputToPaisa } from '@procurement/shared';
import type {
  ProjectCreateInput,
  ProjectCurrencyCode,
  ProjectInspectionLocation,
  ProjectTermsOfDelivery,
} from './project-queries';

export interface NewProjectFormState {
  projectTitle: string;
  projectNumber: string;
  clientId: string;
  projectCurrency: ProjectCurrencyCode;
  otherCurrencyCode: string;
  principalName: string;
  principalAddress: string;
  principalContactPerson: string;
  principalEmail: string;
  principalPhone: string;
  principalFax: string;
  portOfShipment: string;
  principalBankName: string;
  principalBankAccountNumber: string;
  principalBankIban: string;
  principalBankAccountTitle: string;
  principalBankSwiftCode: string;
  principalBankAddress: string;
  principalBankEmail: string;
  principalBankPhone: string;
  principalBankFax: string;
  dfaReceivedDate: string;
  contractSignDate: string;
  deliveryDurationMonths: string;
  gracePeriodDays: string;
  warrantyPeriodMonths: string;
  origin: string;
  indentNumber: string;
  indentDate: string;
  makerNameBrand: string;
  termsOfDelivery: '' | ProjectTermsOfDelivery;
  totalStoreValueInput: string;
  gstPercentInput: string;
  requiredBgAmountInput: string;
  bgSubmissionDurationDays: string;
  bgValidityDurationMonths: string;
  paymentOnINotePercentInput: string;
  paymentOnCrvPercentInput: string;
  partialDeliveryAllowed: boolean;
  partialPaymentAllowed: boolean;
  partialDeliveryPaymentRemarks: string;
  earnestMoneyInput: string;
  consigneeLocation: string;
  defaultInspectionLocation: ProjectInspectionLocation;
  inspectionAuthority: string;
  ndcRequired: boolean;
  ndcReferenceNumber: string;
  ndcRemarks: string;
  remarks: string;
}

function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function parseOptionalInteger(value: string): number | undefined | null {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : null;
}

function parseOptionalRupees(value: string): number | undefined | null {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = parseRupeeInputToPaisa(trimmed);
  return parsed === null || parsed < 0 ? null : parsed;
}

function parseOptionalPercent(value: string): number | undefined | null {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  return parsePercentInputToBps(trimmed);
}

function includeTrimmed(target: ProjectCreateInput, key: keyof ProjectCreateInput, value: string): void {
  const trimmed = trimOrUndefined(value);
  if (trimmed !== undefined) {
    (target as unknown as Record<string, unknown>)[key] = trimmed;
  }
}

const REQUIRED_FOREIGN_DETAIL_FIELDS: ReadonlyArray<keyof Pick<
  NewProjectFormState,
  | 'principalName'
  | 'principalAddress'
  | 'principalContactPerson'
  | 'principalEmail'
  | 'principalPhone'
  | 'principalFax'
  | 'portOfShipment'
  | 'principalBankName'
  | 'principalBankAccountNumber'
  | 'principalBankIban'
  | 'principalBankAccountTitle'
  | 'principalBankSwiftCode'
  | 'principalBankAddress'
  | 'principalBankEmail'
  | 'principalBankPhone'
  | 'principalBankFax'
>> = [
  'principalName',
  'principalAddress',
  'principalContactPerson',
  'principalEmail',
  'principalPhone',
  'principalFax',
  'portOfShipment',
  'principalBankName',
  'principalBankAccountNumber',
  'principalBankIban',
  'principalBankAccountTitle',
  'principalBankSwiftCode',
  'principalBankAddress',
  'principalBankEmail',
  'principalBankPhone',
  'principalBankFax',
];

export function hasCompleteForeignProjectDetails(state: NewProjectFormState): boolean {
  if (state.projectCurrency === 'PKR') return true;
  return REQUIRED_FOREIGN_DETAIL_FIELDS.every((field) => state[field].trim() !== '');
}

export function buildCreateProjectPayload(
  state: NewProjectFormState,
  normalise: {
    name: (s: string) => string;
    projectNumber: (s: string) => string;
  },
): ProjectCreateInput | null {
  const cleanTitle = normalise.name(state.projectTitle);
  const cleanNumber = normalise.projectNumber(state.projectNumber);
  if (!cleanTitle || !cleanNumber || !state.clientId || !state.dfaReceivedDate) {
    return null;
  }

  const cleanOtherCurrencyCode = state.otherCurrencyCode.trim().toUpperCase();
  if (state.projectCurrency === 'OTHER' && cleanOtherCurrencyCode === '') return null;
  if (!hasCompleteForeignProjectDetails(state)) return null;
  if (state.projectCurrency !== 'PKR' && state.termsOfDelivery === 'FOR') return null;

  const totalStoreValuePaisa = parseOptionalRupees(state.totalStoreValueInput);
  const requiredBgAmountPaisa = parseOptionalRupees(state.requiredBgAmountInput);
  const gstRateBps = parseOptionalPercent(state.gstPercentInput);
  const bgSubmissionDays = parseOptionalInteger(state.bgSubmissionDurationDays);
  const bgValidityMonths = parseOptionalInteger(state.bgValidityDurationMonths);
  const paymentOnINoteBps = parseOptionalPercent(state.paymentOnINotePercentInput);
  const paymentOnCrvBps = parseOptionalPercent(state.paymentOnCrvPercentInput);

  // Commercial terms should not be silently dropped when partially/invalidly
  // typed. Keep the legacy earnest-money behavior lenient below for backwards
  // compatibility, but fail commercial payload construction so the modal keeps
  // the user on the form.
  if (
    totalStoreValuePaisa === null ||
    requiredBgAmountPaisa === null ||
    gstRateBps === null ||
    paymentOnINoteBps === null ||
    paymentOnCrvBps === null ||
    bgSubmissionDays === null ||
    bgValidityMonths === null
  ) {
    return null;
  }
  const paymentINotePresent = paymentOnINoteBps !== undefined;
  const paymentCrvPresent = paymentOnCrvBps !== undefined;
  if (paymentINotePresent !== paymentCrvPresent) return null;
  if (
    paymentOnINoteBps !== undefined &&
    paymentOnCrvBps !== undefined &&
    paymentOnINoteBps + paymentOnCrvBps !== 10_000
  ) {
    return null;
  }

  const earnestPaisa = state.earnestMoneyInput.trim()
    ? parseRupeeInputToPaisa(state.earnestMoneyInput)
    : null;

  const payload: ProjectCreateInput = {
    projectTitle: cleanTitle,
    projectNumber: cleanNumber,
    clientId: state.clientId,
    dfaReceivedDate: state.dfaReceivedDate,
    projectCurrency: state.projectCurrency,
  };

  if (state.projectCurrency === 'OTHER') payload.otherCurrencyCode = cleanOtherCurrencyCode;
  includeTrimmed(payload, 'principalName', state.principalName);
  includeTrimmed(payload, 'principalAddress', state.principalAddress);
  includeTrimmed(payload, 'principalContactPerson', state.principalContactPerson);
  includeTrimmed(payload, 'principalEmail', state.principalEmail);
  includeTrimmed(payload, 'principalPhone', state.principalPhone);
  includeTrimmed(payload, 'principalFax', state.principalFax);
  includeTrimmed(payload, 'portOfShipment', state.portOfShipment);
  includeTrimmed(payload, 'principalBankName', state.principalBankName);
  includeTrimmed(payload, 'principalBankAccountNumber', state.principalBankAccountNumber);
  includeTrimmed(payload, 'principalBankIban', state.principalBankIban);
  includeTrimmed(payload, 'principalBankAccountTitle', state.principalBankAccountTitle);
  includeTrimmed(payload, 'principalBankSwiftCode', state.principalBankSwiftCode);
  includeTrimmed(payload, 'principalBankAddress', state.principalBankAddress);
  includeTrimmed(payload, 'principalBankEmail', state.principalBankEmail);
  includeTrimmed(payload, 'principalBankPhone', state.principalBankPhone);
  includeTrimmed(payload, 'principalBankFax', state.principalBankFax);

  if (state.contractSignDate) payload.contractSignDate = state.contractSignDate;
  if (state.deliveryDurationMonths) payload.deliveryDurationMonths = Number(state.deliveryDurationMonths);
  if (state.gracePeriodDays) payload.gracePeriodDays = Number(state.gracePeriodDays);
  if (state.warrantyPeriodMonths) payload.warrantyPeriodMonths = Number(state.warrantyPeriodMonths);
  includeTrimmed(payload, 'origin', state.origin);
  includeTrimmed(payload, 'indentNumber', state.indentNumber);
  if (state.indentDate) payload.indentDate = state.indentDate;
  includeTrimmed(payload, 'makerNameBrand', state.makerNameBrand);
  if (state.termsOfDelivery) payload.termsOfDelivery = state.termsOfDelivery;
  if (totalStoreValuePaisa !== undefined) payload.totalStoreValuePaisa = totalStoreValuePaisa;
  if (gstRateBps !== undefined) payload.gstRateBps = gstRateBps;
  if (requiredBgAmountPaisa !== undefined) payload.requiredBgAmountPaisa = requiredBgAmountPaisa;
  if (bgSubmissionDays !== undefined) payload.bgSubmissionDurationDays = bgSubmissionDays;
  if (bgValidityMonths !== undefined) payload.bgValidityDurationMonths = bgValidityMonths;
  if (paymentOnINoteBps !== undefined) payload.paymentOnINoteBps = paymentOnINoteBps;
  if (paymentOnCrvBps !== undefined) payload.paymentOnCrvBps = paymentOnCrvBps;
  if (state.partialDeliveryAllowed) payload.partialDeliveryAllowed = true;
  if (state.partialPaymentAllowed) payload.partialPaymentAllowed = true;
  includeTrimmed(payload, 'partialDeliveryPaymentRemarks', state.partialDeliveryPaymentRemarks);
  if (earnestPaisa !== null && earnestPaisa >= 0) payload.earnestMoneyAmountPaisa = earnestPaisa;
  includeTrimmed(payload, 'consigneeLocation', state.consigneeLocation);
  payload.defaultInspectionLocation = state.defaultInspectionLocation;
  includeTrimmed(payload, 'inspectionAuthority', state.inspectionAuthority);
  if (state.ndcRequired) payload.ndcRequired = true;
  includeTrimmed(payload, 'ndcReferenceNumber', state.ndcReferenceNumber);
  includeTrimmed(payload, 'ndcRemarks', state.ndcRemarks);
  includeTrimmed(payload, 'remarks', state.remarks);

  return payload;
}
